import React, { useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { LOGO_URL } from "../lib/i18n";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const COPY = {
  es: {
    eyebrow: "Sitio en construcción",
    titleA: "Pronto.",
    titleB: "Algo bueno",
    titleC: "está creciendo.",
    body: "Estamos preparando la presentación de Agrovalor Iberica — pistacho sostenible con Agricultura 4.0. Si quieres recibir el dossier o saber cuándo abrimos el sitio, déjanos tu correo.",
    emailPh: "tu@correo.com",
    namePh: "Tu nombre (opcional)",
    cta: "Avísame",
    sending: "Enviando…",
    success: "Gracias. Te avisaremos.",
    error: "No pudimos registrar tu correo. Inténtalo de nuevo.",
    contact: "Para temas urgentes",
    location: "Agrovalor Iberica SL · España",
    altLang: "EN",
    tagline: "Pistacho · Sostenibilidad · Agricultura 4.0",
    progress: "Lanzamiento próximo",
  },
  en: {
    eyebrow: "Site under construction",
    titleA: "Soon.",
    titleB: "Something good",
    titleC: "is growing.",
    body: "We are getting ready to introduce Agrovalor Iberica — sustainable pistachio powered by Agriculture 4.0. Drop your email if you want the dossier or a heads-up when we go live.",
    emailPh: "you@email.com",
    namePh: "Your name (optional)",
    cta: "Notify me",
    sending: "Sending…",
    success: "Thank you. We will be in touch.",
    error: "We couldn't register your email. Please try again.",
    contact: "For urgent matters",
    location: "Agrovalor Iberica SL · Spain",
    altLang: "ES",
    tagline: "Pistachio · Sustainability · Agriculture 4.0",
    progress: "Launching soon",
  },
  fr: {
    eyebrow: "Site en construction",
    titleA: "Bientôt.",
    titleB: "Quelque chose de bon",
    titleC: "est en train de pousser.",
    body: "Nous préparons la présentation d'Agrovalor Iberica — pistache durable avec Agriculture 4.0. Laissez votre email si vous souhaitez recevoir le dossier ou être informé du lancement.",
    emailPh: "vous@email.com",
    namePh: "Votre nom (optionnel)",
    cta: "Prévenez-moi",
    sending: "Envoi…",
    success: "Merci. Nous reviendrons vers vous.",
    error: "Nous n'avons pas pu enregistrer votre email. Réessayez.",
    contact: "Pour les sujets urgents",
    location: "Agrovalor Iberica SL · Espagne",
    altLang: "ES",
    tagline: "Pistache · Durabilité · Agriculture 4.0",
    progress: "Lancement prochain",
  },
};

export default function ComingSoon() {
  const { lang, toggle } = useLang();
  const c = COPY[lang];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", {
        name: name || "(sin nombre)",
        email,
        phone: "",
        message: lang === "es"
          ? "Suscripción desde la página Coming Soon."
          : "Subscribed from the Coming Soon page.",
        type: "waitlist",
        locale: lang,
      });
      setDone(true);
      toast.success(c.success);
      setName("");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || c.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="coming-soon-page" className="min-h-screen relative overflow-hidden bg-white">
      {/* Decorative italic word */}
      <div
        aria-hidden
        className="hidden lg:block absolute -right-16 top-1/2 -translate-y-1/2 vertical-rl font-serif italic text-[14rem] leading-none text-olive/[0.05] select-none"
      >
        agrovalor
      </div>

      {/* Top bar */}
      <header className="absolute top-0 inset-x-0 z-10">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="block w-2 h-2 rounded-full bg-terracotta animate-pulse" aria-hidden />
            <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-olive-soft">
              {c.progress}
            </span>
          </div>
          <button
            onClick={toggle}
            data-testid="cs-lang-toggle"
            className="font-mono text-[10px] tracking-[0.3em] uppercase text-olive border border-olive/20 px-3 py-2 hover:bg-olive hover:text-ivory transition-colors"
          >
            <span className={lang === "es" ? "opacity-100" : "opacity-40"}>ES</span>
            <span className="opacity-30 mx-1">/</span>
            <span className={lang === "en" ? "opacity-100" : "opacity-40"}>EN</span>
            <span className="opacity-30 mx-1">/</span>
            <span className={lang === "fr" ? "opacity-100" : "opacity-40"}>FR</span>
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 min-h-screen grid grid-cols-12 gap-y-12 gap-x-6 items-center py-32 relative">
        {/* Logo column */}
        <div className="col-span-12 lg:col-span-5 flex flex-col items-start animate-fade-up">
          <img
            src={LOGO_URL}
            alt="Agrovalor Iberica"
            className="w-48 sm:w-64 h-auto object-contain mb-10"
            style={{ mixBlendMode: "darken" }}
            data-testid="cs-logo"
          />
          <div className="flex items-start gap-3 mb-4">
            <span className="block h-px w-10 bg-terracotta mt-3" aria-hidden />
            <span className="eyebrow">{c.eyebrow}</span>
          </div>
          <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-olive-soft mt-2">
            {c.tagline}
          </div>
        </div>

        {/* Text + form column */}
        <div className="col-span-12 lg:col-span-7">
          <h1
            data-testid="cs-headline"
            className="h-hero text-olive mb-10 animate-fade-up"
            style={{ animationDelay: "120ms" }}
          >
            {c.titleA}
            <br />
            <span className="italic text-terracotta">{c.titleB}</span>
            <br />
            {c.titleC}
          </h1>

          <p
            className="font-serif text-xl sm:text-2xl text-olive-soft leading-snug max-w-2xl mb-12 animate-fade-up"
            style={{ animationDelay: "240ms" }}
          >
            {c.body}
          </p>

          {done ? (
            <div data-testid="cs-success" className="border border-olive/15 p-8 max-w-xl animate-fade-up">
              <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-terracotta mb-3">✓</div>
              <p className="font-display text-3xl text-olive">{c.success}</p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="cs-form"
              className="grid grid-cols-1 sm:grid-cols-12 gap-x-6 gap-y-4 max-w-2xl animate-fade-up"
              style={{ animationDelay: "320ms" }}
            >
              <div className="sm:col-span-5">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={c.namePh}
                  data-testid="cs-name"
                  className="input-editorial"
                />
              </div>
              <div className="sm:col-span-7">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={c.emailPh}
                  data-testid="cs-email"
                  className="input-editorial"
                />
              </div>
              <div className="sm:col-span-12 mt-2">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="cs-submit"
                  className="bg-olive text-ivory px-7 py-4 font-mono text-[11px] tracking-[0.28em] uppercase hover:bg-terracotta transition-all duration-500 hover:tracking-[0.32em] disabled:opacity-50"
                >
                  {loading ? c.sending : `${c.cta} →`}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Footer strip */}
      <footer className="absolute bottom-0 inset-x-0 border-t border-olive/10">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-olive-soft">
          <span>{c.location}</span>
          <a
            href="mailto:info@agrovaloriberica.com"
            className="text-terracotta link-underline"
            data-testid="cs-email-link"
          >
            {c.contact} · info@agrovaloriberica.com
          </a>
        </div>
      </footer>
    </main>
  );
}
